import { FC, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import { Tab } from '@mui/material';
import { isShipmentUnderReview } from 'src/utils/invoiceStatus';
import { useTourContext } from 'src/presentations/guided-tours';
import { useShipmentSelector } from '../ShipmentSelector';
import { featureFlags } from 'src/config/featureFlags';
import { MainDetailsTabsProps } from './types';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '48px!important',
  '& .MuiTabs-flexContainer': {
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  '& .MuiTabs-scroller': {
    overflowX: 'visible !important',
  },
  '& .MuiTab-root': {
    fontSize: '16px',
    fontWeight: '500',
    textTransform: 'capitalize',
    boxSizing: 'border-box',
    color: '#9F9F9F',
    maxWidth: 'none',
    height: '40px',
    padding: '6px 10px',
    backgroundColor: '#34424C',
    border: '1px solid #545C61',
    borderRadius: '8px',
    boxShadow: 'inset 0 4px 7px 0 rgba(0,0,0,0.4)',
    transition: 'all 0.2s ease-in-out',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.26)',
      boxShadow: '0px 4px 7px 0px rgba(0, 0, 0, 0.45) inset',
    },
    '&.Mui-selected': {
      backgroundColor: '#778188',
      borderColor: '#dfdfdf',
      color: '#ffffff',
      boxShadow: 'none',
    },
  },
  '& .MuiTabs-indicator': {
    opacity: 0,
  },
}));

export const MainDetailsTabs: FC<MainDetailsTabsProps> = (props) => {
  const { tabIndex, onChange, invoiceSummary } = props;
  const { shipment } = useShipmentSelector();
  const tour = useTourContext().appTour.registerTour('actionDetails');

  const onChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    onChange(newValue);
  };

  const hasDeviationsNotif = useMemo(() => {
    if (shipment) {
      if (isShipmentUnderReview(invoiceSummary, shipment?.shipmentInvoiceId)) {
        return false;
      }
      const isHasNotDone = shipment?.deviations?.some((item) => item?.status !== 'DONE' && item?.status !== 'REJECTED_DONE');
      const isAllowProcurement =
        invoiceSummary?.currentMainRole === 'PROCUREMENT' &&
        shipment?.currentDeviationWorkflow === 'PROCUREMENT_REVIEW' &&
        shipment?.deviations?.some((item) => item?.type === 'NEW_CHARGE');
      const isAllowOperation =
        invoiceSummary?.currentMainRole === 'OPERATION' &&
        shipment?.currentDeviationWorkflow === 'OPERATION_REVIEW' &&
        shipment?.deviations?.some((item) => item?.type !== 'NEW_CHARGE');
      if (isHasNotDone && (isAllowProcurement || isAllowOperation)) {
        return true;
      }
    }
    return false;
  }, [shipment, invoiceSummary]);

  const hasApprovalNotif = useMemo(
    () =>
      invoiceSummary?.currentMainRole === 'OPERATION' &&
      (shipment?.approvalTypeApprovalStatusMap?.ACCESSORIAL_CHARGE === 'PENDING' || shipment?.approvalTypeApprovalStatusMap?.SERVICE_TYPE === 'PENDING'),
    [shipment?.approvalTypeApprovalStatusMap, invoiceSummary?.currentMainRole]
  );

  const isShowDisputeResultTab = useMemo(() => {
    if (invoiceSummary?.status === 'FLAGGED' && invoiceSummary?.invoiceDispute && invoiceSummary?.invoiceDispute?.status === 'CANCELED') {
      return true;
    }
    return invoiceSummary?.status === 'REJECTED' || invoiceSummary?.status === 'APPROVED' || invoiceSummary?.status === 'TO_APPROVE';
  }, [invoiceSummary]);

  return (
    <div id={tour.registerStep('deviationsApprovalsTabs')} className='bg-blue_grey-90 py-8 xl:py-10 px-14 xl:px-18'>
      <StyledTabs className='flex flex-1' value={tabIndex} onChange={onChangeTab} aria-label='uct tab'>
        {featureFlags?.SHOW_INVOICE_APPROVALS ? (
          <Tab className='flex-grow' label={<div className='relative'>Invoice Approvals</div>} disableRipple />
        ) : (
          <Tab
            className='flex-grow'
            label={
              <div className='relative'>
                Approvals
                {hasApprovalNotif && <span className='absolute top-6 -right-18 w-8 h-8 rounded-full bg-[#F79824]' />}
              </div>
            }
            disableRipple
          />
        )}
        <Tab
          className='flex-grow'
          label={
            <div className='relative'>
              {featureFlags?.SHOW_INVOICE_APPROVALS ? (
                <div>Shipments Deviations</div>
              ) : (
                <>
                  Deviations
                  {hasDeviationsNotif && <span className='absolute top-6 -right-18 w-8 h-8 rounded-full bg-[#F79824]' />}
                </>
              )}
            </div>
          }
          disableRipple
        />
        {isShowDisputeResultTab && <Tab className='flex-grow' label={<div className='relative'>Dispute Result</div>} disableRipple />}
      </StyledTabs>
    </div>
  );
};
